import { Card, Skeleton, SkeletonAvatar, SkeletonRegion } from '@parsina/framework';
import { C, RADIUS } from '@parsina/framework/tokens';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';

// ─────────────────────────────────────────────────────────────────────────────
// The messages screen's loading shapes — two regions that load independently.
//
// `ConversationListSkeleton`: one `ConversationRow` / `MobileConversationCard`
// per placeholder (Card, 0.75rem padding, a 38px avatar on desktop and 44px on
// the phone, name + preview lines, the time and an unread pill on the far
// side). `ThreadSkeleton`: message bubbles alternating sides, the way a real
// thread reads, so the pane keeps its identity while the room loads.
// ─────────────────────────────────────────────────────────────────────────────

const ConversationRowSkeleton = ({ i, avatar }) => (
  <Card aria-hidden style={{ padding: '0.75rem', borderRadius: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
    <SkeletonAvatar size={avatar} />
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
      <Skeleton width={`${44 + ((i * 17) % 30)}%`} height={13} />
      <Skeleton width={`${60 + ((i * 11) % 30)}%`} height={11} />
    </div>
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
      <Skeleton width={36} height={10} />
      {i % 3 === 0 ? <Skeleton width={20} height={20} radius={RADIUS.pill} /> : null}
    </div>
  </Card>
);

export const ConversationListSkeleton = ({ count = 5, avatar = 38, label }) => {
  const { t } = useTranslation();
  return (
    <SkeletonRegion label={label ?? t('common.state.loading')} data-testid="messages-list-loading" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      {Array.from({ length: count }, (_, i) => <ConversationRowSkeleton key={i} i={i} avatar={avatar} />)}
    </SkeletonRegion>
  );
};

const BUBBLES = [
  { mine: false, w: '62%', h: 44 },
  { mine: true,  w: '48%', h: 36 },
  { mine: false, w: '70%', h: 58 },
  { mine: true,  w: '40%', h: 36 },
  { mine: false, w: '55%', h: 36 },
];

export const ThreadSkeleton = ({ label }) => {
  const { t } = useTranslation();
  return (
    <SkeletonRegion label={label ?? t('common.state.loading')} data-testid="messages-thread-loading" style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
      {BUBBLES.map((b, i) => (
        <div key={i} aria-hidden style={{ display: 'flex', justifyContent: b.mine ? 'flex-end' : 'flex-start' }}>
          <Skeleton
            width={b.w} height={b.h} radius={RADIUS.card}
            style={{ [b.mine ? 'borderEndEndRadius' : 'borderEndStartRadius']: RADIUS.chip, border: `1px solid ${C.border}` }}
          />
        </div>
      ))}
    </SkeletonRegion>
  );
};

export default ConversationListSkeleton;
